/* globals zoomSdk */
import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { ErrorBoundary } from 'react-error-boundary'
import { useSelector, useDispatch } from 'react-redux'
import InputBox from './InputBox'
import Recommendations from './Recommendations'
import GeneratedImages from './GeneratedImages'
import ArtisticStyles from './ArtisticStyles'
import NotificationMessage from './NotificationMessage'
import {
  resetAddImage,
  resetTextToImage,
  setTextToImageError,
  setTextToImages,
} from '../vb/actions'
import {
  selectTextToImagesError,
  selectTextToImagesLoading,
  selectTextToImagesRequestId,
  selectTextToImagesSuccess,
} from '../vb/reducer'
import { selectZaConfig, selectZaConfigError } from '../root/reducer'
import useLightModePreference from '../util/useLightModePreference'
import {
  selectIsResultCleared,
  selectErrorMessage,
  selectQuery,
} from './reducer'
import * as CONSTANTS from './constants'
import { setIsNotificationVisible } from './actions'
import GeneralErrorScreen from './GeneralErrorScreen'
import {
  logAIVirtualBackgroundDisplayed,
  logGenerateButtonClicked,
} from '../telemetry/telemetry'
import './ClientSetting.css'
import { initSnowplow } from '../telemetry/telemetry'

const ErrorFallback = () => {
  return <GeneralErrorScreen />
}

const ClientSetting = () => {
  const { i18n } = useTranslation()
  const [generatedImagesKey, setGeneratedImagesKey] = useState(0) // key to trigger re-render
  const isClientSettingPreview =
    new URLSearchParams(window.location.search).get('view') === 'clientSetting'
  const dispatch = useDispatch()
  const textToImagesLoading = useSelector(selectTextToImagesLoading)
  const zaConfigOutput = useSelector(selectZaConfig)
  const zaConfigError = useSelector(selectZaConfigError)
  const isLightMode = useLightModePreference()
  const errorMessage = useSelector(selectErrorMessage)
  const textToImageError = useSelector(selectTextToImagesError)
  const textToImageSuccess = useSelector(selectTextToImagesSuccess)
  const isResultCleared = useSelector(selectIsResultCleared)
  const requestId = useSelector(selectTextToImagesRequestId)
  const query = useSelector(selectQuery)
  const queryLength = query?.trim().length || 0
  const showGeneratedImages =
    (textToImagesLoading || textToImageSuccess) &&
    !errorMessage &&
    !textToImageError &&
    queryLength &&
    !isResultCleared

  useEffect(() => {
    if (zaConfigOutput) {
      initSnowplow()
        .then(() => {
          // Log the display event after Snowplow is initialized
          logAIVirtualBackgroundDisplayed()
        })
        .catch((error) => {
          console.error('Client Setting: Snowplow initialization failed', error)
        })
    }
  }, [zaConfigOutput])

  useEffect(() => {
    if (!queryLength) {
      dispatch(resetTextToImage())
      dispatch(setIsNotificationVisible(false))
      dispatch(resetAddImage())
    }
  }, [queryLength, dispatch])

  useEffect(() => {
    const zmOnTextToImage = (event) => {
      if (event.requestId !== requestId) return
      if (event.images?.length > 0 && !errorMessage && !textToImageError) {
        dispatch(setTextToImages(event.images))
      } else {
        const isContentModerationError =
          event.code === 100002 ||
          event.code === 100003 ||
          event.code === 100004

        const errorMessage = isContentModerationError
          ? 'content moderation'
          : CONSTANTS.ERROR_MESSAGE_FAILED_TO_GENERATE

        dispatch(setTextToImageError(errorMessage))
        logGenerateButtonClicked({ errorReason: errorMessage })
      }
    }

    if (zaConfigOutput) {
      window.zoomSdk.addEventListener('zmOnTextToImage', zmOnTextToImage)
    }

    if (zaConfigOutput) {
      return () => {
        window.zoomSdk.removeEventListener('zmOnTextToImage', zmOnTextToImage)
      }
    }
  }, [zaConfigOutput, errorMessage, textToImageError, requestId, dispatch])

  useEffect(() => {
    const getLanguage = async () => {
      const { language } = await zoomSdk.zmGetLanguage()
      i18n.changeLanguage(language === 'pt-pt' ? 'pt' : language)
    }

    if (zaConfigOutput) {
      getLanguage()
    }
  }, [zaConfigOutput, i18n])

  // When press 'tab' on the last element on the landing page, it should go back to the first element, and vice versa.
  useEffect(() => {
    const handleTabKey = (e) => {
      if (e.key === 'Tab') {
        const firstElement = document.querySelector('input')
        const lastElement =
          document.querySelector('.lastArtisticStyle') ||
          document.querySelector('.lastGeneratedImage') ||
          document.querySelector('.lastLoadingImage')

        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            e.preventDefault()
            lastElement?.focus()
          }
        } else {
          if (document.activeElement === lastElement) {
            e.preventDefault()
            firstElement?.focus()
          }
        }
      }
    }

    document.addEventListener('keydown', handleTabKey)
    return () => {
      document.removeEventListener('keydown', handleTabKey)
    }
  }, [])

  useEffect(() => {
    const handleError = async () => {
      if (zaConfigError) {
        logAIVirtualBackgroundDisplayed({ errorReason: zaConfigError })
      }
    }
    handleError()
  }, [zaConfigError])

  if (zaConfigError && !isClientSettingPreview) {
    return <GeneralErrorScreen />
  }

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <div
        className={`ClientSetting ${!isLightMode ? 'dark-mode' : ''} ${
          !textToImageError ? 'no-error' : ''
        }`}
      >
        <InputBox
          showGeneratedImages={showGeneratedImages}
          setGeneratedImagesKey={setGeneratedImagesKey}
        />
        <div className="ClientSetting__content">
          {showGeneratedImages ? (
            <>
              <GeneratedImages key={generatedImagesKey} />
              <ArtisticStyles showImage={false} />
            </>
          ) : (
            <>
              <Recommendations />
              <ArtisticStyles />
            </>
          )}
        </div>
        <NotificationMessage />
      </div>
    </ErrorBoundary>
  )
}

export default ClientSetting
